// src/components/admin/batches/BatchCreationForm.tsx
import React, { useEffect, useState } from "react";
import { useBillingStore } from "src/store/billingStore";
import { useTutorStore } from "src/store/tutorStore";
import { useBatchCreationStore } from "src/store/batchCreationStore";
import { Button } from "src/components/ui/Button";
import { Label } from "src/components/ui/Label";
import { Input } from "src/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/Select";
import {
  Check,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Sparkles,
  RefreshCw,
  Package,
} from "lucide-react";
import { format } from "date-fns";
import { TutorMatchmakingModal } from "./TutorMatchmakingModal";
import { TutorMatchCriteria } from "src/types/tutorMatchmaking";

interface BatchCreationFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const BatchCreationForm: React.FC<BatchCreationFormProps> = ({ onSuccess, onCancel }) => {
  const { fetchProducts } = useBillingStore();
  const {
    fetchTutors,
    tutors,
    isLoading: isLoadingTutors,
    error: tutorError,
  } = useTutorStore();

  const {
    selectedStudent,
    selectedTutor,
    selectedTutorMatchScore,
    selectedSubscription,
    enrolledStudents,
    selectedStudentData,
    batchName,
    internalNotes,
    isLoadingStudents,
    isLoading,
    error,
    success,
    setSelectedTutor,
    setSelectedTutorMatchInfo,
    setSelectedSubscription,
    setBatchName,
    setInternalNotes,
    regenerateBatchName,
    fetchEnrolledStudents,
    handleStudentSelection,
    handleSubmit,
  } = useBatchCreationStore();

  const [isMatchmakingOpen, setIsMatchmakingOpen] = useState(false);

  useEffect(() => {
    fetchEnrolledStudents();
    fetchTutors();
    fetchProducts();

    return () => {
      useBatchCreationStore.getState().resetForm();
    };
  }, [fetchEnrolledStudents, fetchTutors, fetchProducts]);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        onSuccess();
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [success, onSuccess]);

  const selectedSubscriptionData = selectedStudentData?.subscriptions.find(
    (sub) => sub.id === selectedSubscription
  );

  const selectedTutorObj = tutors.find((t) => t.id === selectedTutor);
  const selectedTutorName = selectedTutorObj
    ? `${selectedTutorObj.first_name} ${selectedTutorObj.last_name}`
    : selectedTutor !== "none" && selectedTutor
    ? "Assigned Tutor"
    : null;

  const handleTutorPickedFromModal = (
    tutorId: string,
    tutorName: string,
    matchScore: number,
    matchLabel: string
  ) => {
    setSelectedTutor(tutorId);
    setSelectedTutorMatchInfo(matchScore, matchLabel);
    setIsMatchmakingOpen(false);
  };

  const matchmakingCriteria: TutorMatchCriteria = {
    subjectName: selectedSubscriptionData?.subject_name || "",
    educationLevel: selectedStudentData?.education_level || null,
    board: selectedStudentData?.board || null,
  };

  const isReadyToProvision =
    Boolean(selectedStudent) &&
    Boolean(selectedSubscription) &&
    Boolean(batchName.trim()) &&
    !isLoading &&
    !success;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
      {/* ========================================================================= */}
      {/* Milestone Progress Card Header (Option A Refined)                         */}
      {/* ========================================================================= */}
      <div className="p-3.5 sm:p-4 bg-slate-50/80 border-b border-slate-200">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {/* Milestone 1: Student Enrolment */}
          <div
            className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition ${
              selectedStudentData
                ? "bg-purple-50/90 border-purple-200 text-purple-900 shadow-2xs"
                : "bg-white/60 border-slate-200 text-slate-500"
            }`}
          >
            <span
              className={`w-6 h-6 rounded-full font-bold text-xs flex items-center justify-center shrink-0 ${
                selectedStudentData
                  ? "bg-purple-600 text-white"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              {selectedStudentData ? <Check className="w-3.5 h-3.5" /> : "1"}
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-purple-950 truncate">
                1. Student Enrolment
              </p>
              <p className="text-[11px] text-purple-700 font-medium truncate">
                {selectedStudentData
                  ? `${selectedStudentData.first_name} ${selectedStudentData.last_name}`
                  : "Select student"}
              </p>
            </div>
          </div>

          {/* Milestone 2: Tutor Pairing & Capacity Check */}
          <div
            className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition ${
              selectedTutor && selectedTutor !== "none"
                ? "bg-white border-2 border-purple-600 shadow-xs text-slate-900"
                : "bg-white/60 border-slate-200 text-slate-500"
            }`}
          >
            <span
              className={`w-6 h-6 rounded-full font-bold text-xs flex items-center justify-center shrink-0 ${
                selectedTutor && selectedTutor !== "none"
                  ? "bg-purple-600 text-white ring-2 ring-purple-100"
                  : "bg-slate-200 text-slate-600"
              }`}
            >
              2
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-bold text-purple-900 truncate">
                2. Tutor Pairing & Capacity
              </p>
              <p className="text-[11px] text-slate-600 font-medium truncate">
                {selectedTutorName
                  ? `${selectedTutorName} ${
                      selectedTutorMatchScore ? `(${selectedTutorMatchScore}%)` : ""
                    }`
                  : "Optional pairing"}
              </p>
            </div>
          </div>

          {/* Milestone 3: Batch Identity & Dispatch */}
          <div
            className={`flex items-center gap-2.5 p-2.5 rounded-lg border transition ${
              isReadyToProvision
                ? "bg-emerald-50/80 border-emerald-200 text-emerald-900 shadow-2xs"
                : "bg-white/60 border-slate-200 text-slate-400"
            }`}
          >
            <span
              className={`w-6 h-6 rounded-full font-semibold text-xs flex items-center justify-center shrink-0 ${
                isReadyToProvision
                  ? "bg-emerald-600 text-white"
                  : "bg-slate-100 text-slate-500 border border-slate-200"
              }`}
            >
              3
            </span>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold truncate text-slate-800">
                3. Batch Identity & Review
              </p>
              <p className="text-[11px] text-slate-500 truncate">
                {isReadyToProvision ? "Ready for Dispatch" : "Awaiting details"}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ========================================================================= */}
      {/* Form Content                                                             */}
      {/* ========================================================================= */}
      <form onSubmit={handleSubmit}>
        <div className="p-6 space-y-6">
          {/* Phase 1: Student & Enrollment Match */}
          <div className="space-y-4 pb-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                  1
                </span>
                <h4 className="text-sm font-bold text-slate-900">
                  Student & Active Enrolment
                </h4>
              </div>
              {selectedStudentData && (
                <span className="text-xs font-medium text-emerald-600 flex items-center gap-1">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Student Selected
                </span>
              )}
            </div>

            {/* Student Select Dropdown */}
            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="student" className="text-xs font-semibold text-slate-700">
                  Enrolled Student <span className="text-rose-500">*</span>
                </Label>
                <span className="text-xs text-slate-500">
                  {isLoadingStudents
                    ? "Loading active enrolments..."
                    : `${enrolledStudents.length} active enrolment${
                        enrolledStudents.length !== 1 ? "s" : ""
                      } awaiting batches`}
                </span>
              </div>

              {isLoadingStudents ? (
                <div className="w-full h-11 px-3.5 bg-slate-50 border border-slate-200 rounded-lg flex items-center text-xs text-slate-500">
                  <Loader2 className="w-4 h-4 animate-spin mr-2 text-purple-600" />
                  Loading students awaiting batches...
                </div>
              ) : enrolledStudents.length === 0 ? (
                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600">
                  No enrolled students awaiting batch assignment.
                </div>
              ) : (
                <Select value={selectedStudent} onValueChange={handleStudentSelection}>
                  <SelectTrigger id="student" className="h-11 border-slate-300">
                    <SelectValue placeholder="Select an enrolled student" />
                  </SelectTrigger>
                  <SelectContent>
                    {enrolledStudents.map((student) => (
                      <SelectItem key={student.id} value={student.id}>
                        <div className="flex items-center gap-2 text-xs">
                          <span className="font-semibold text-slate-900">
                            {student.first_name} {student.last_name}
                          </span>
                          <span className="text-slate-400 font-mono">({student.email})</span>
                          {student.education_level && (
                            <span className="px-1.5 py-0.5 rounded bg-purple-50 text-purple-700 text-[10px] font-semibold border border-purple-200">
                              {student.education_level}
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* Subscription Selection */}
            {selectedStudentData && (
              <div className="space-y-2 pt-1">
                <Label className="text-xs font-semibold text-slate-700">
                  Enrolled Package Subscription <span className="text-rose-500">*</span>
                </Label>

                {selectedStudentData.subscriptions.length === 0 ? (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 text-amber-600" />
                    This student has no active subscriptions available for batch provisioning.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedStudentData.subscriptions.map((sub) => {
                      const isSelected = sub.id === selectedSubscription;
                      return (
                        <div
                          key={sub.id}
                          onClick={() => setSelectedSubscription(sub.id)}
                          className={`p-3.5 rounded-lg border transition cursor-pointer flex items-center justify-between ${
                            isSelected
                              ? "border-purple-300 bg-purple-50/50 shadow-2xs"
                              : "border-slate-200 hover:border-purple-200 bg-white"
                          }`}
                        >
                          <div className="flex items-center gap-3">
                            <div
                              className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                isSelected
                                  ? "bg-purple-100 text-purple-700"
                                  : "bg-slate-100 text-slate-500"
                              }`}
                            >
                              <Package className="w-4 h-4" />
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs font-bold text-slate-900">
                                  {sub.product_name}
                                </span>
                                {sub.sessions_count && (
                                  <span className="px-1.5 py-0.2 bg-purple-100 text-purple-800 rounded text-[10px] font-semibold">
                                    {sub.sessions_count} Sessions
                                  </span>
                                )}
                                <span className="px-1.5 py-0.2 bg-emerald-100 text-emerald-800 rounded text-[10px] font-semibold">
                                  Active Entitlement
                                </span>
                              </div>
                              <p className="text-[11px] text-slate-500 mt-0.5">
                                Expires:{" "}
                                <span className="font-medium text-slate-700">
                                  {format(new Date(sub.current_period_end), "MMM d, yyyy")}
                                </span>{" "}
                                ({sub.days_remaining} days left) • Subject:{" "}
                                <span className="font-semibold text-purple-700">
                                  {sub.subject_name || "General"}
                                </span>
                              </p>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 text-xs font-semibold">
                            {isSelected ? (
                              <span className="text-purple-700 flex items-center gap-1">
                                Selected <Check className="w-3.5 h-3.5 text-purple-600" />
                              </span>
                            ) : (
                              <span className="text-slate-400 hover:text-purple-600">
                                Select →
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Phase 2: Tutor Pairing & Academic Alignment */}
          <div className="space-y-4 pb-6 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                  2
                </span>
                <h4 className="text-sm font-bold text-slate-900">
                  Tutor Assignment & Subject Specialization
                </h4>
              </div>
              <span className="text-xs text-slate-500">Can be updated post-dispatch</span>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="tutor" className="text-xs font-semibold text-slate-700">
                  Assign Default Lead Tutor
                </Label>

                {/* Matchmaking Assistant Trigger Button */}
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setIsMatchmakingOpen(true)}
                  disabled={!selectedStudent || !selectedSubscription}
                  className="h-8 px-2.5 text-xs font-semibold text-purple-700 bg-purple-50 hover:bg-purple-100 border-purple-200 transition shadow-2xs hover:border-purple-300 flex items-center gap-1.5"
                >
                  <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                  Find Recommended Tutors (Calculate Matches)
                </Button>
              </div>

              {/* Standard Dropdown */}
              <div className="relative">
                <Select
                  value={selectedTutor}
                  onValueChange={(val) => {
                    setSelectedTutor(val);
                    setSelectedTutorMatchInfo(null, null);
                  }}
                  disabled={isLoadingTutors}
                >
                  <SelectTrigger id="tutor" className="h-11 border-slate-300">
                    <SelectValue placeholder={isLoadingTutors ? "Loading tutors..." : "Select a tutor"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No default tutor (Defer assignment)</SelectItem>
                    {tutors.map((tutor) => (
                      <SelectItem key={tutor.id} value={tutor.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-slate-900">
                            {tutor.first_name} {tutor.last_name}
                          </span>
                          {tutor.rating && (
                            <span className="text-xs text-slate-500 font-mono">
                              ⭐ {tutor.rating}
                            </span>
                          )}
                          {tutor.id === selectedTutor && selectedTutorMatchScore && (
                            <span className="px-1.5 py-0.2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded text-[10px] font-bold">
                              {selectedTutorMatchScore}% Match
                            </span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {tutorError && (
                <p className="text-xs text-red-600">Unable to load tutors: {tutorError}</p>
              )}

              <div className="flex items-center justify-between text-xs text-slate-500 pt-0.5">
                <span>
                  {selectedTutor && selectedTutor !== "none" ? (
                    <>
                      Assigned: <strong className="text-slate-800">{selectedTutorName}</strong>
                      {selectedTutorMatchScore && (
                        <span className="ml-1.5 text-emerald-700 font-semibold font-mono">
                          ({selectedTutorMatchScore}% compatibility)
                        </span>
                      )}
                    </>
                  ) : (
                    "Leave unassigned to queue batch as needing tutor pairing."
                  )}
                </span>
                {selectedStudent && selectedSubscription && (
                  <button
                    type="button"
                    onClick={() => setIsMatchmakingOpen(true)}
                    className="text-purple-700 hover:underline font-medium"
                  >
                    Open matchmaking assistant →
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Phase 3: Batch Code & Display Identity */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-purple-600 text-white text-xs font-bold flex items-center justify-center">
                3
              </span>
              <h4 className="text-sm font-bold text-slate-900">
                Batch Code & Display Identity
              </h4>
            </div>

            <div className="space-y-1.5">
              <div className="flex items-center justify-between">
                <Label htmlFor="batch-name" className="text-xs font-semibold text-slate-700">
                  Canonical Batch Name <span className="text-rose-500">*</span>
                </Label>
                {selectedSubscription && (
                  <button
                    type="button"
                    onClick={regenerateBatchName}
                    className="text-xs text-purple-700 hover:text-purple-800 font-semibold flex items-center gap-1"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Regenerate Name
                  </button>
                )}
              </div>
              <Input
                id="batch-name"
                value={batchName}
                onChange={(e) => setBatchName(e.target.value)}
                placeholder="Select a student and subscription to generate a name"
                disabled={!selectedSubscription}
                className="h-10 text-sm border-slate-300 font-medium"
              />
              <p className="text-xs text-slate-500">
                Auto-generated from student, subject, educational board, and billing cohort. Visible to parents and tutors.
              </p>
            </div>

            {/* Internal Operational Notes */}
            <div className="space-y-1.5 pt-1">
              <Label htmlFor="notes" className="text-xs font-semibold text-slate-700">
                Internal Operational Notes (Optional)
              </Label>
              <textarea
                id="notes"
                rows={2}
                value={internalNotes}
                onChange={(e) => setInternalNotes(e.target.value)}
                placeholder="e.g. Student prefers weekday evenings after 6 PM IST. Requested mechanics focus."
                className="w-full p-2.5 bg-white border border-slate-300 rounded-lg text-xs text-slate-900 focus:ring-2 focus:ring-purple-600 focus:border-purple-600 outline-none transition"
              />
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-lg text-xs text-rose-800 flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
              <span>Batch created and launched successfully! Redirecting...</span>
            </div>
          )}
        </div>

        {/* ========================================================================= */}
        {/* Card Footer Controls                                                      */}
        {/* ========================================================================= */}
        <div className="px-6 py-4 bg-slate-50 border-t border-slate-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <span className="text-xs text-slate-500 flex items-center gap-1.5">
            {isReadyToProvision ? (
              <>
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span className="text-emerald-700 font-medium">
                  All prerequisites met for batch provisioning
                </span>
              </>
            ) : (
              <span>Select student and package to complete batch configuration</span>
            )}
          </span>

          <div className="flex items-center gap-2.5 self-end sm:self-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onCancel}
              disabled={isLoading || success}
              className="h-9 px-4 text-xs font-semibold text-slate-700"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!isReadyToProvision}
              className="h-9 px-5 text-xs font-semibold text-white bg-purple-600 hover:bg-purple-700 shadow-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                  Provisioning...
                </>
              ) : (
                "Create & Launch Batch"
              )}
            </Button>
          </div>
        </div>
      </form>

      {/* ========================================================================= */}
      {/* On-Demand Tutor Matchmaking Modal                                          */}
      {/* ========================================================================= */}
      <TutorMatchmakingModal
        isOpen={isMatchmakingOpen}
        onClose={() => setIsMatchmakingOpen(false)}
        onSelectTutor={handleTutorPickedFromModal}
        criteria={matchmakingCriteria}
      />
    </div>
  );
};

export default BatchCreationForm;
